import { isDesktopHost, waitForDesktopElectronApi } from '@/lib/desktop-host'

export const DESKTOP_SESSION_ACCESS_TIMEOUT_MS = 10000

export type DesktopSessionSnapshot = {
  isAuthenticated?: boolean
  authRecoveryPending?: boolean
  authRecoveryReason?: string | null
  authRecoveryRetryAt?: string | null
  user?: {
    id?: string | null
    email?: string | null
  } | null
  sessionData?: {
    accessToken?: string | null
    refreshToken?: string | null
    tokenType?: string | null
    expiresAt?: number | null
    expiresIn?: number | null
  } | null
  error?: string | null
}

async function getDesktopSessionAccessor(timeoutMs?: number) {
  if (typeof window === 'undefined' || !isDesktopHost()) {
    return null
  }

  const electronApi = await waitForDesktopElectronApi({
    timeoutMs
  })

  return electronApi?.desktop?.session?.get || electronApi?.getDesktopSession || null
}

export async function loadDesktopSessionSnapshot(options?: {
  includeTokens?: boolean
  timeoutMs?: number
}): Promise<DesktopSessionSnapshot | null> {
  const timeoutMs =
    typeof options?.timeoutMs === 'number' && options.timeoutMs > 0
      ? Math.round(options.timeoutMs)
      : DESKTOP_SESSION_ACCESS_TIMEOUT_MS
  const getDesktopSession = await getDesktopSessionAccessor(timeoutMs)
  if (typeof getDesktopSession !== 'function') {
    return null
  }

  const sessionPromise = Promise.resolve(
    getDesktopSession({
      includeTokens: options?.includeTokens === true
    })
  ) as Promise<DesktopSessionSnapshot | null>

  const timeoutPromise = new Promise<DesktopSessionSnapshot | null>((resolve) => {
    window.setTimeout(() => resolve(null), timeoutMs)
  })

  try {
    return await Promise.race([sessionPromise, timeoutPromise])
  } catch (error) {
    console.warn('Failed to load desktop session snapshot:', error)
    return null
  }
}
